/**
 * Individual E-shop Page
 * Shows customers, abandoned carts, and consultations for a specific e-shop
 */

import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { useDataSourceStore } from '@store/dataSourceStore';
import { api } from '../services/api';
import {
  ArrowLeft,
  Users,
  ShoppingCart,
  Settings,
  RefreshCw,
  AlertCircle,
  Database,
  Send,
} from 'lucide-react';

// Tab components
import EshopCustomersTab from '../components/eshop/EshopCustomersTab';
import EshopShoppingCartsTab from '../components/eshop/EshopShoppingCartsTab';
import SmartSyncModal from '../components/eshop/SmartSyncModal';
import QuickIntegrationsModal from '../components/eshop/QuickIntegrationsModal';

type TabType = 'customers' | 'shopping-carts';

interface EshopDetails {
  id: string;
  name: string;
  type: string;
  purpose?: string;
  config: any;
  isActive: boolean;
}

const EshopDetailPage: React.FC = () => {
  const { eshopId } = useParams<{ eshopId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuthStore();
  const { getSelection } = useDataSourceStore();

  // Get the selected company from the data source store
  const selection = getSelection();
  const companyId = selection?.companyId;

  const [activeTab, setActiveTab] = useState<TabType>('customers');
  const [eshop, setEshop] = useState<EshopDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSmartSyncModal, setShowSmartSyncModal] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);

  useEffect(() => {
    fetchEshopDetails();
  }, [eshopId, companyId]);

  const fetchEshopDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!companyId || !eshopId) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      // Fetch company data to get the specific e-shop data source
      const response = await api.get(`/api/v1/companies/${companyId}`);
      const data = response.data;

      // Find the specific e-shop data source
      const eshopDataSource = (data.data.dataSources || []).find(
        (ds: any) =>
          (ds.id === eshopId || ds._id === eshopId) && ds.availableModules?.includes('eshop')
      );

      if (!eshopDataSource) {
        throw new Error('E-shop not found');
      }

      // Add company settings to eshop data
      eshopDataSource.companySettings = data.data.settings;

      setEshop(eshopDataSource);
    } catch (err: any) {
      console.error('Error fetching e-shop details:', err);
      setError(err.message || 'Failed to load e-shop details');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'customers' as TabType, label: 'Customers', icon: Users },
    { id: 'shopping-carts' as TabType, label: 'Shopping Carts', icon: ShoppingCart },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error || !eshop) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Error Loading E-shop
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{error || 'E-shop not found'}</p>
          <button
            onClick={() => navigate('/eshops')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to E-shops
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/eshops')}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-indigo-600" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{eshop.name}</h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {eshop.purpose || 'E-commerce database integration'}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowIntegrationsModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg shadow-green-600/30"
            title="Configure GetResponse & Email Marketing Integrations"
          >
            <Send className="w-4 h-4" />
            Integrations
          </button>
          <button
            onClick={() => setShowSmartSyncModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg shadow-indigo-600/30"
            title="Sync PostgreSQL e-shop data to MongoDB"
          >
            <Settings className="w-4 h-4" />
            Smart Sync
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Tab Headers */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex gap-2 p-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'customers' && (
            <EshopCustomersTab eshopId={eshop.id} eshopConfig={eshop.config} />
          )}
          {activeTab === 'shopping-carts' && (
            <EshopShoppingCartsTab
              eshopId={eshop.id}
              eshopConfig={eshop.config}
              companyCurrency={eshop.companySettings?.currency || 'GBP'}
            />
          )}
        </div>
      </div>

      {/* Smart Sync Modal */}
      {showSmartSyncModal && eshop && (
        <SmartSyncModal
          isOpen={showSmartSyncModal}
          onClose={() => setShowSmartSyncModal(false)}
          eshopId={eshop.id}
          eshopName={eshop.name}
          companyId={companyId || ''}
        />
      )}

      {/* Quick Integrations Modal - Simplified */}
      {showIntegrationsModal && eshop && (
        <QuickIntegrationsModal
          eshop={eshop}
          isOpen={showIntegrationsModal}
          onClose={() => {
            setShowIntegrationsModal(false);
            // Refresh eshop details to get updated integration settings
            fetchEshopDetails();
          }}
        />
      )}
    </div>
  );
};

export default EshopDetailPage;
